/**
 * indexRowMath.ts
 * Item 5.2 — pure FE mirror of the Phase-2 ("Index tests") row math so the
 * panel can show a live per-day fractional count + % contribution WITHOUT a
 * round-trip on every window edit.
 *
 * ⚠️ SOURCE OF TRUTH: `backend/src/calculation/indexCapacity.ts`. This module
 * intentionally re-derives the same three functions (`workingDaysInWindow`,
 * `validateIndexWindow`, `evenSpreadPerDay`) plus a row-level %-contribution
 * helper the backend doesn't need (its `dayUtilizationPct` sums ACROSS
 * allocations for a shared day; this panel shows ONE row's own contribution).
 * Any change to holiday/window/spread semantics on the backend MUST be
 * mirrored here — this is presentation-only optimism, not an alternate rule
 * set. The PATCH response (persisted TestCount/StartDate/EndDate) is always
 * the eventual source of truth; this module only avoids a flash of stale
 * numbers between the optimistic UI update and the server round-trip.
 */

// ---------------------------------------------------------------------------
// Constants (mirrors indexCapacity.ts)
// ---------------------------------------------------------------------------

/**
 * Fallback capacity (tests/day) mirroring `INDEX_UNKNOWN_CAPACITY_FALLBACK`
 * (A2 seed default) — engages only when a TaskType is flagged `IsIndexTest`
 * before an admin sets its `IndexTestsPerDay`.
 */
export const INDEX_UNKNOWN_CAPACITY_FALLBACK = 8;

// ---------------------------------------------------------------------------
// Types (mirrors indexCapacity.ts)
// ---------------------------------------------------------------------------

export type IndexWindowValidationError = 'INVALID_RANGE' | 'ZERO_WORKING_DAYS';

export interface IndexWindowValidation {
  valid: boolean;
  workingDays: number;
  error: IndexWindowValidationError | null;
  message: string | null;
}

export interface EvenSpreadResult {
  ok: boolean;
  perDayCount: number;
  workingDays: number;
  error: IndexWindowValidationError | null;
  message: string | null;
}

/** A single row's %-of-capacity contribution (own TaskType only — not the shared-day sum). */
export interface RowContributionResult {
  /** perDayCount ÷ capacityPerDay × 100, rounded to 1 decimal. */
  pct: number;
  /** true when `capacityPerDay` was null/zero/negative and the fallback was used. */
  usedFallbackCapacity: boolean;
}

// ---------------------------------------------------------------------------
// Working-day window math (mirrors schedulingCalculations.countWorkingDays)
// ---------------------------------------------------------------------------

/** Mon–Fri check, holiday-aware — mirrors backend `isWorkingDay`. */
function isWorkingDay(dateIso: string, holidaySet: ReadonlySet<string>): boolean {
  const dow = new Date(`${dateIso}T00:00:00Z`).getUTCDay();
  if (dow === 0 || dow === 6) return false;
  return !holidaySet.has(dateIso);
}

/** Add `days` calendar days to an ISO date string, returning ISO 'YYYY-MM-DD'. */
function addDays(dateIso: string, days: number): string {
  const d = new Date(`${dateIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Count the working days (Mon–Fri, holiday-aware) in the inclusive window
 * [startDate, endDate]. Mirrors `indexCapacity.ts`'s `workingDaysInWindow`,
 * itself a thin wrapper over `schedulingCalculations.countWorkingDays`.
 *
 * @returns 0 when startDate > endDate or either is empty.
 */
export function workingDaysInWindow(
  startDate: string,
  endDate: string,
  holidayDates: readonly string[],
): number {
  if (!startDate || !endDate || startDate > endDate) return 0;
  const holidaySet = new Set(holidayDates.map((h) => h.slice(0, 10)));
  let count = 0;
  let current = startDate;
  // Bounded walk — guards against a pathological/typo'd multi-decade window.
  for (let guard = 0; current <= endDate && guard < 20000; guard++) {
    if (isWorkingDay(current, holidaySet)) count++;
    current = addDays(current, 1);
  }
  return count;
}

/**
 * Validate a planning window (I22) — mirrors `indexCapacity.ts`'s
 * `validateIndexWindow`. Never throws; typed error result only.
 */
export function validateIndexWindow(
  startDate: string,
  endDate: string,
  holidayDates: readonly string[],
): IndexWindowValidation {
  if (!startDate || !endDate || startDate > endDate) {
    return {
      valid: false,
      workingDays: 0,
      error: 'INVALID_RANGE',
      message: `Window end date (${endDate || '(empty)'}) must be on or after the start date (${startDate || '(empty)'}).`,
    };
  }

  const workingDays = workingDaysInWindow(startDate, endDate, holidayDates);
  if (workingDays === 0) {
    return {
      valid: false,
      workingDays: 0,
      error: 'ZERO_WORKING_DAYS',
      message: `The window ${startDate}–${endDate} contains no working days (all weekends and/or stat holidays) — choose a window with at least one working day.`,
    };
  }

  return { valid: true, workingDays, error: null, message: null };
}

// ---------------------------------------------------------------------------
// Even spread (I10) — mirrors indexCapacity.ts's evenSpreadPerDay
// ---------------------------------------------------------------------------

/**
 * Spread `testCount` evenly across the working days of [startDate, endDate],
 * fractional (I10). Mirrors `indexCapacity.ts`'s `evenSpreadPerDay` exactly —
 * `ok: false` on a zero-working-day/invalid window (never NaN/throw).
 */
export function evenSpreadPerDay(
  testCount: number,
  startDate: string,
  endDate: string,
  holidayDates: readonly string[],
): EvenSpreadResult {
  const validation = validateIndexWindow(startDate, endDate, holidayDates);
  if (!validation.valid) {
    return {
      ok: false,
      perDayCount: 0,
      workingDays: validation.workingDays,
      error: validation.error,
      message: validation.message,
    };
  }

  const safeCount = Number.isFinite(testCount) ? Math.max(0, testCount) : 0;
  return {
    ok: true,
    perDayCount: safeCount / validation.workingDays,
    workingDays: validation.workingDays,
    error: null,
    message: null,
  };
}

// ---------------------------------------------------------------------------
// Row %-contribution (panel-only — not a backend day-sum)
// ---------------------------------------------------------------------------

/**
 * One row's own %-of-capacity contribution: `perDayCount ÷ capacityPerDay ×
 * 100`. This is NOT the shared-day utilization sum (`dayUtilizationPct` in
 * `indexCapacity.ts` / the Item 5.3 heatmap sums every active allocation on a
 * day) — it's a simpler per-row "how much of this TaskType's daily capacity
 * does this one allocation use" figure for the Phase-2 table.
 *
 * Null/zero/negative capacity falls back to `INDEX_UNKNOWN_CAPACITY_FALLBACK`
 * (mirroring the backend fallback) and flags `usedFallbackCapacity` so the UI
 * can badge the assumption instead of presenting it as authoritative.
 */
export function rowContributionPct(
  perDayCount: number,
  capacityPerDay: number | null | undefined,
): RowContributionResult {
  const usedFallbackCapacity =
    capacityPerDay == null || !Number.isFinite(capacityPerDay) || capacityPerDay <= 0;
  const capacity = usedFallbackCapacity ? INDEX_UNKNOWN_CAPACITY_FALLBACK : capacityPerDay;
  const safePerDay = Number.isFinite(perDayCount) ? perDayCount : 0;
  const pct = Math.round((safePerDay / capacity) * 100 * 10) / 10;
  return { pct, usedFallbackCapacity };
}

/** Heatmap band (mirrors `indexCapacity.ts`'s `indexCapacityBand`): 0–70 low / 71–100 mid / >100 high. */
export type IndexCapacityBand = 'low' | 'mid' | 'high';

/** Maps a %-contribution to the shared heatmap band token (theme.schedule.heatmap*). */
export function contributionBand(pct: number): IndexCapacityBand {
  const p = Number.isFinite(pct) ? pct : 0;
  if (p > 100) return 'high';
  if (p > 70) return 'mid';
  return 'low';
}
